import moment from "moment";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import relativeTime from "dayjs/plugin/relativeTime";
import _ from "underscore";
import { getInterfaceLanguage } from "./LangPrefs";
import { isStandardPayPlanForUser, resolvePayPlan, PAY_PLAN_APP, WEB_APP_CODE } from "./PayPlanUtil";
import {
    PUZZLE_COINS_APP,
    readCoinsEarned,
    readTotalPuzzleCoinsFromPlay,
    resolveJigsawMuseCoins,
    resolveSlidingQuestCoins,
    resolveTotalPuzzleCoins,
    resolveWebPuzzleCoins,
} from "./PuzzleCoinsUtil";

import "moment/locale/fr";
import "moment/locale/zh-cn";
import "moment/locale/hi";
import "moment/locale/es";
import "moment/locale/ar";
import "moment/locale/pt";
import "moment/locale/de";
import "moment/locale/ja";
import "moment/locale/ko";

dayjs.extend(duration);
dayjs.extend(relativeTime);

/** Values from `ui.lang` / {@link getInterfaceLanguage} → moment locale id */
const MOMENT_LOCALE_BY_INTERFACE = {
    english: "en",
    french: "fr",
    chinese: "zh-cn",
    hindi: "hi",
    spanish: "es",
    arabic: "ar",
    portuguese: "pt",
    german: "de",
    japanese: "ja",
    korean: "ko",
};

function interfaceLangToMomentLocale(interfaceLang) {
    if (!interfaceLang || typeof interfaceLang !== "string") return "en";
    return MOMENT_LOCALE_BY_INTERFACE[interfaceLang] || "en";
}

/** UI language → BCP 47 for {@link Intl.DateTimeFormat} (readable dates without mixed separators). */
const INTL_LOCALE_BY_INTERFACE = {
    english: "en-US",
    french: "fr-FR",
    chinese: "zh-CN",
    hindi: "hi-IN",
    spanish: "es-ES",
    arabic: "ar",
    portuguese: "pt-BR",
    german: "de-DE",
    japanese: "ja-JP",
    korean: "ko-KR",
};

function interfaceLangToIntlLocale(interfaceLang) {
    if (!interfaceLang || typeof interfaceLang !== "string") return "en-US";
    return INTL_LOCALE_BY_INTERFACE[interfaceLang] || "en-US";
}

/**
 * Some APIs return Unix time in **seconds**; JavaScript & moment expect **milliseconds**.
 * A seconds value (e.g. 1.7e9) parsed as ms lands near 1970 and shows "56 years ago".
 * @param {number|string|Date} value
 * @returns {number|string|Date|undefined} Pass-through for Date; ISO strings unchanged; number → ms.
 */
function toEpochMsForMoment(value) {
    if (value == null || value === 0) return undefined;
    if (value instanceof Date) return value;
    if (typeof value === "string" && (value.includes("T") || /^\d{4}-\d{2}/.test(value))) {
        return value;
    }
    const n = Number(value);
    if (!Number.isFinite(n) || n < 0) return undefined;
    /** Seconds since epoch for ~1980–2049; ms for that range is 3e11–1e15 (all > SEC_MAX) */
    const SEC_MIN = 315_532_800; // 1980-01-01
    const SEC_MAX = 2_500_000_000; // ~2049
    if (n >= SEC_MIN && n <= SEC_MAX) {
        return n * 1000;
    }
    return n;
}

const UtilService = {
    formatDate: (timestamp) => {
        if (timestamp == undefined || timestamp == 0) return;
        const t = toEpochMsForMoment(timestamp);
        if (t == null) return;
        return moment(t).format("YYYY/MM/DD HH:mm");
    },

    formatDateHM: (timestamp) => {
        if (timestamp == undefined || timestamp == 0) return;
        const t = toEpochMsForMoment(timestamp);
        if (t == null) return;
        return moment(t).format("D MMM YYYY, HH:mm");
    },

    formatMDY: (timestamp) => {
        if (timestamp == undefined || timestamp == 0) return;
        const t = toEpochMsForMoment(timestamp);
        if (t == null) return;
        const d = new Date(t);
        if (Number.isNaN(d.getTime())) return;
        try {
            return new Intl.DateTimeFormat(interfaceLangToIntlLocale(getInterfaceLanguage()), {
                year: "numeric",
                month: "short",
                day: "numeric",
            }).format(d);
        } catch {
            return moment(t).format("YYYY-MM-DD");
        }
    },

    isEmpty: function (element) {
        if (element === null || element === "undefined" || _.isEmpty(element) || (_.isArray(element) && element.length === 0)) {
            return true;
        }
        return false;
    },

    toFahrenheit: function (value, fractionDigits = 2) {
        return parseFloat((value * 1.8 + 32).toFixed(fractionDigits));
    },

    toCelsius: function (value, fractionDigits = 2) {
        return parseFloat(((value - 32) / 1.8).toFixed(fractionDigits));
    },

    toCubicFeet: (gallons, fractionDigits = 1) => {
        return (gallons * 0.133681).toFixed(fractionDigits);
    },

    formatRelativeTime: (timestamp) => {
        if (timestamp == undefined || timestamp == 0) return;
        const t = toEpochMsForMoment(timestamp);
        if (t == null) return;
        return moment(t).fromNow(); // e.g., "2 hours ago"
    },

    formatHumanize: (timestamp) => {
        if (!timestamp || timestamp <= 0) {
            return null;
        }

        const dur = dayjs.duration(timestamp);
        return dur.humanize();
    },

    /**
     * Formats a duration in ms into a localized relative string (e.g. "2 months ago", "il y a 2 mois").
     * Uses moment.js locales; wording is a single humanized span (not a custom English-only phrase list).
     * @param {number} diffMs - Elapsed milliseconds (e.g. Date.now() - pastDate)
     * @param {string} [interfaceLang] - App UI language key (e.g. "french"); defaults to {@link getInterfaceLanguage}
     * @returns {string|null}
     */
    formatHumanizeDetailed: (diffMs, interfaceLang) => {
        if (!diffMs || diffMs <= 0) return null;
        const langKey = interfaceLang || getInterfaceLanguage();
        const momentLoc = interfaceLangToMomentLocale(langKey);
        const previousLocale = moment.locale();
        try {
            moment.locale(momentLoc);
            const text = moment.duration(-diffMs, "milliseconds").humanize(true);
            return text && String(text).trim() ? text : null;
        } finally {
            moment.locale(previousLocale);
        }
    },

    formatCompletionTime: (startedAt, finishedAt) => {
        if (!startedAt || !finishedAt) {
            return null;
        }

        const s = toEpochMsForMoment(startedAt);
        const f = toEpochMsForMoment(finishedAt);
        if (s == null || f == null) {
            return null;
        }
        const startTime = new Date(s);
        const endTime = new Date(f);
        const durationMs = endTime - startTime;
        
        return UtilService.formatHumanize(durationMs);
    },

    /**
     * True if the user has a Standard (paid) subscription.
     * @param {string} payPlan - The pay plan to check
     * @returns {boolean}
     */
    isStandardPayPlan: (payPlan) => {
        return payPlan === 'STANDARD' || payPlan === 'PREMIUM';
    },

    /** App keys for cloudUser.payPlans (web, sliding_puzzle_quest, jigsaw_muse). */
    PAY_PLAN_APP,

    /** Web credit / coin / assistant API appCode (`mo_apps.code` = web_app). */
    WEB_APP_CODE,

    /** App keys for cloudUser.puzzleCoins (sliding_puzzle_quest, jigsaw_muse, total). */
    PUZZLE_COINS_APP,

    resolveJigsawMuseCoins,
    resolveSlidingQuestCoins,
    resolveTotalPuzzleCoins,
    resolveWebPuzzleCoins,
    readCoinsEarned,
    readTotalPuzzleCoinsFromPlay,

    /**
     * Web app entitlement from GET /home/user (payPlans.web).
     * @param {object | null | undefined} cloudUser
     * @returns {string}
     */
    resolveWebPayPlan: (cloudUser) => resolvePayPlan(cloudUser, PAY_PLAN_APP.WEB),

    /**
     * True when the user has STANDARD or PREMIUM for the given app (default web).
     * @param {object | null | undefined} cloudUser
     * @param {string} [appCode]
     * @returns {boolean}
     */
    isStandardPayPlanForUser: (cloudUser, appCode) => isStandardPayPlanForUser(cloudUser, appCode),

};

export default UtilService;
